import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faGlobe } from "@fortawesome/free-solid-svg-icons";
import {
  MIN_ADJUSTMENT_PERCENT,
  MAX_ADJUSTMENT_PERCENT,
  NO_EFFECT_ADJUSTMENT_PERCENT,
  buildAdjustmentGrid,
  getAdjustmentKey,
  isAllowedAdjustmentText,
  isValidAdjustmentPercent,
  normalizeAdjustmentPercent,
} from "../utils/productivityAdjustments";

/**
 * The complete productivity adjustment matrix — the same data as the
 * Mobilization Costs → Productivity Adjustments page — rendered as a review and
 * edit surface that can sit beside another form.
 *
 * These values are GLOBAL: each one belongs to a method + damage combination
 * pair and applies to every element using that method. Nothing here is scoped
 * to whatever the host form is editing; `highlightMethodIds` only tints rows so
 * the user can spot the methods they are currently working with.
 */
const ProductivityAdjustmentsPanel = ({
  matrix,
  onSave,
  disabled = false,
  highlightMethodIds = null,
  manageAllUrl = "",
}) => {
  const [overrides, setOverrides] = useState({});
  const [methodFilter, setMethodFilter] = useState("all");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedNotice, setSavedNotice] = useState(false);
  // Last in-range value per cell, so a field emptied mid-edit can be restored
  // on blur instead of being reported as an error.
  const lastValidRef = useRef({});

  const adjustments = useMemo(() => matrix?.adjustments || [], [matrix]);
  const methods = useMemo(() => matrix?.accessMethods || [], [matrix]);

  // Drop local edits whenever the server matrix changes (e.g. after a save
  // refetch), so the panel never shows stale overrides on top of fresh data.
  const baseSignature = useMemo(
    () =>
      adjustments
        .map((row) => `${getAdjustmentKey(row)}:${row.productivityPercentage}`)
        .join(","),
    [adjustments],
  );

  useEffect(() => {
    setOverrides({});
    lastValidRef.current = {};
    setSavedNotice(false);
  }, [baseSignature]);

  useEffect(() => {
    if (methodFilter === "all") return;
    const stillExists = methods.some(
      (method) =>
        String(method.accessMethodSystemListId) === String(methodFilter),
    );
    if (!stillExists) setMethodFilter("all");
  }, [methodFilter, methods]);

  const methodSortIndex = useMemo(
    () =>
      new Map(
        methods.map((method, index) => [
          String(method.accessMethodSystemListId),
          index,
        ]),
      ),
    [methods],
  );

  const visibleAdjustments = useMemo(() => {
    if (methodFilter === "all") return adjustments;
    return adjustments.filter(
      (row) =>
        String(row.accessMethodSystemListId) === String(methodFilter),
    );
  }, [adjustments, methodFilter]);

  const grid = useMemo(
    () => buildAdjustmentGrid(visibleAdjustments, methodSortIndex),
    [visibleAdjustments, methodSortIndex],
  );

  const baseValueByKey = useMemo(() => {
    const map = new Map();
    adjustments.forEach((row) => {
      map.set(getAdjustmentKey(row), Number(row.productivityPercentage));
    });
    return map;
  }, [adjustments]);

  const valueFor = (row) => {
    const key = getAdjustmentKey(row);
    if (Object.prototype.hasOwnProperty.call(overrides, key)) {
      return overrides[key];
    }
    const base = baseValueByKey.get(key);
    return base === undefined || base === null
      ? NO_EFFECT_ADJUSTMENT_PERCENT
      : base;
  };

  const handleChange = (row, raw) => {
    // A keystroke that would produce a leading zero or exceed the ceiling never
    // lands; the field keeps whatever it already held.
    if (!isAllowedAdjustmentText(raw)) return;
    setSavedNotice(false);
    const key = getAdjustmentKey(row);
    const next = raw === "" ? "" : String(raw);
    if (isValidAdjustmentPercent(next)) lastValidRef.current[key] = next;
    setOverrides((prev) => ({ ...prev, [key]: next }));
  };

  // Leaving a cell always commits an in-range value: round a valid entry to two
  // decimals, or restore the last in-range one if the field was left empty or
  // half-typed. Nothing is substituted for a minimum — the previous value
  // simply comes back.
  const handleBlur = (row) => {
    const key = getAdjustmentKey(row);
    const current = overrides[key];
    if (current === undefined) return;

    if (isValidAdjustmentPercent(current)) {
      const normalized = String(normalizeAdjustmentPercent(current));
      lastValidRef.current[key] = normalized;
      if (normalized !== String(current)) {
        setOverrides((prev) => ({ ...prev, [key]: normalized }));
      }
      return;
    }

    const base = baseValueByKey.get(key);
    const fallback =
      lastValidRef.current[key] ??
      String(base === undefined || base === null
        ? NO_EFFECT_ADJUSTMENT_PERCENT
        : base);
    setOverrides((prev) => ({ ...prev, [key]: fallback }));
  };

  const dirtyRows = useMemo(() => {
    const rows = [];
    Object.entries(overrides).forEach(([key, value]) => {
      if (!isValidAdjustmentPercent(value)) return;
      const pct = Math.round(Number(value) * 100) / 100;
      if (pct === Number(baseValueByKey.get(key))) return;
      const [comboId, methodId] = key.split("|").map((part) => Number(part));
      rows.push({
        corrosionDamageCombinationId: comboId,
        accessMethodSystemListId: methodId,
        productivityPercentage: pct,
      });
    });
    return rows;
  }, [overrides, baseValueByKey]);

  const handleSave = async () => {
    if (dirtyRows.length === 0) return;
    try {
      setSaving(true);
      setError("");
      await onSave(dirtyRows);
      setSavedNotice(true);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to save productivity adjustments.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!matrix) {
    return (
      <div className="p-4 text-sm text-gray-500">
        Loading productivity adjustments...
      </div>
    );
  }

  const highlighted = highlightMethodIds
    ? new Set(Array.from(highlightMethodIds).map((id) => String(id)))
    : null;

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-gray-200 px-4 py-3">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900">
            Productivity Adjustments
          </h3>
          <span className="text-[10px] uppercase tracking-wide text-gray-500">
            Global
          </span>
        </div>

        <div className="mt-2 flex items-start gap-2 rounded border border-blue-200 bg-blue-50 px-2.5 py-2 text-xs text-blue-900">
          <FontAwesomeIcon icon={faGlobe} className="mt-0.5 flex-shrink-0" />
          <div>
            <p>
              <span className="font-semibold">Not element settings.</span> Each
              value belongs to a method + damage combination pair and applies to
              every element using that method, everywhere.
            </p>
            <p className="mt-1">
              <span className="font-semibold">100%</span> = the method has no
              effect on base productivity. Lower it (e.g. 85) to slow it down.
            </p>
          </div>
        </div>

        {methods.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setMethodFilter("all")}
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                methodFilter === "all"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              All methods
            </button>
            {methods.map((method) => {
              const id = String(method.accessMethodSystemListId);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setMethodFilter(id)}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                    methodFilter === id
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {method.accessMethodName}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {grid.groups.length === 0 ? (
          <div className="px-4 py-6 text-sm text-gray-500">
            {adjustments.length === 0
              ? "No damage combinations or access methods are configured yet."
              : "No adjustments for the selected method."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Class
                </th>
                {grid.severities.map((severity) => (
                  <th
                    key={`sev-${severity.code}`}
                    className="px-1.5 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500"
                    title={severity.name || severity.code}
                  >
                    {severity.code}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.groups.map((group) => (
                <React.Fragment key={`method-${group.methodId}`}>
                  <tr>
                    <td
                      colSpan={grid.severities.length + 1}
                      className={`border-y border-gray-200 px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                        highlighted?.has(group.methodId)
                          ? "bg-blue-100 text-blue-900"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {group.methodName}
                      {highlighted?.has(group.methodId) && (
                        <span className="ml-2 font-medium normal-case tracking-normal text-blue-700">
                          in this split
                        </span>
                      )}
                    </td>
                  </tr>
                  {group.rows.map((classRow) => (
                    <tr
                      key={`${group.methodId}-${classRow.classCode}`}
                      className="border-b border-gray-100"
                    >
                      <td
                        className="whitespace-nowrap px-3 py-1 font-medium text-gray-900"
                        title={classRow.className || classRow.classCode}
                      >
                        {classRow.classCode}
                      </td>
                      {classRow.cells.map((cell) => {
                        if (!cell.row) {
                          return (
                            <td
                              key={`${group.methodId}-${classRow.classCode}-${cell.severityCode}`}
                              className="px-1.5 py-1 text-center text-gray-300"
                            >
                              —
                            </td>
                          );
                        }
                        const value = valueFor(cell.row);
                        const isNoEffect =
                          Number(value) === NO_EFFECT_ADJUSTMENT_PERCENT;
                        const inputClass = isNoEffect
                          ? "border-amber-300 bg-amber-50 text-gray-700"
                          : "border-blue-300 bg-blue-50 font-semibold text-blue-800";
                        return (
                          <td
                            key={getAdjustmentKey(cell.row)}
                            className="px-1.5 py-1 text-center"
                          >
                            <input
                              type="text"
                              inputMode="decimal"
                              value={value}
                              disabled={disabled || saving}
                              onChange={(event) =>
                                handleChange(cell.row, event.target.value)
                              }
                              onBlur={() => handleBlur(cell.row)}
                              onFocus={(event) => event.target.select()}
                              title={`${cell.row.combinationCode} - ${cell.row.accessMethodName}`}
                              aria-label={`${cell.row.accessMethodName} productivity for ${cell.row.combinationCode}`}
                              className={`w-full min-w-[3.25rem] rounded border px-1 py-1 text-center ${inputClass}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="border-t border-gray-200 px-4 py-3">
        <p className="text-xs text-gray-500">
          Whole or decimal values from {MIN_ADJUSTMENT_PERCENT} to{" "}
          {MAX_ADJUSTMENT_PERCENT}. Amber cells are at 100% (no effect).
        </p>

        {error && (
          <div className="mt-2 rounded border border-red-200 bg-red-50 px-2.5 py-2 text-xs text-red-700">
            {error}
          </div>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={disabled || saving || dirtyRows.length === 0}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                Saving...
              </>
            ) : (
              `Save adjustments${
                dirtyRows.length ? ` (${dirtyRows.length})` : ""
              }`
            )}
          </button>
          {savedNotice && dirtyRows.length === 0 && (
            <span className="text-xs text-green-700">Adjustments saved.</span>
          )}
          {manageAllUrl && (
            <a
              href={manageAllUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto text-xs font-medium text-blue-700 underline hover:text-blue-900"
            >
              Open full page ↗
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductivityAdjustmentsPanel;
